import createSelector from 'selectorator';
import { isEmpty, isUndefined, some, sortBy, values } from 'underscore';
import * as ProjectsV2Store from '../projectsV2';
import type { Project, TeamProject } from '../../types';
import { getQueryTeamId } from '../utils';

const getProjects = createSelector(['projects.projects']);
const getProjectList = createSelector([getProjects], values);

const REFERENCE_TYPE_BRANCH = 'branch';

const sortFns = {
  stats: (projects) => sortBy(projects, (project: Project) => {
    return (project.revisions ?? [])[0] ? project.revisions[0].dependency_count : -1;
  }),
  status: (projects) => sortBy(projects, (project: Project) => {
    return (project.revisions ?? [])[0] ? project.revisions[0].unresolved_issue_count : -1;
  }),
  title: (projects) => sortBy(projects, (project: Project) => project.title?.toLowerCase() ?? -1),
  updatedAt: (projects) => sortBy(projects, 'updatedAt'),
};

// ProjectsPage query param selectors
const getFilterString = (state, props) => props.location.query.title;
const getLocator = (state, props) => props.locator || props.projectId;
const getBranch = (state, props) => props.branch;

const getSortMode = state => state.projects.sortMode;

const isReferenceTypeBranch = reference => reference.type === REFERENCE_TYPE_BRANCH;

const getFilteredProjects = createSelector([getProjectList, getFilterString], (allProjects, filterString = '') => {
  return allProjects.filter(project => project && project.title && project.title.toLowerCase().includes(filterString.toLowerCase()));
});

const getProjectsByTeam = createSelector([getFilteredProjects, getQueryTeamId], (filteredProjects, teamId: string | undefined) => {
  if (isUndefined(teamId)) {
    return filteredProjects;
  }

  // unassigned projects
  if (teamId === 'null') {
    return filteredProjects.filter((project) => isEmpty(project.teamProjects));
  }

  return filteredProjects.filter((project) => some(project.teamProjects, (teamProject: TeamProject) => teamProject.teamId === Number(teamId)));
});

const getSortedProjects = createSelector([getProjectsByTeam, getSortMode], (filteredProjects, sortMode = '') => {
  const sortFn = sortFns[sortMode.slice(sortMode.indexOf('-') + 1, sortMode.length)];
  if (sortFn) {
    const sortedProjects = sortFn(filteredProjects);
    const isDesc = sortMode.includes('-');
    if (isDesc) {
      sortedProjects.reverse();
    }
    return sortedProjects;
  }
  return filteredProjects;
});

// This will select the projectv2 store
// This is only used by create release group
const getSelectedProjects = createSelector(
  [getSortedProjects, ProjectsV2Store.selectors.getSelectedProjectLocators],
  (projects, selectedIds = []) => selectedIds === 'all' ?
    'all' : projects.filter(project => selectedIds.includes(project.locator)),
);

const getProjectByLocator = createSelector(
  [getProjectList, getLocator],
  (projects, locator) => projects ? projects.find(project => project.locator === locator) : null,
);

const getProjectReferences = createSelector(
  [getProjectByLocator],
  (project) => project ? project.references : [],
);

const getBranchesForProject = createSelector(
  [getProjectReferences],
  (references) => references ? references.filter(isReferenceTypeBranch) : [],
);

const getProjectIsFetchingBranches = createSelector(
  [getProjectByLocator],
  (project) => project ? project.isFetchingBranches : false,
);

const getRevisionsForProject = createSelector(
  [getProjectByLocator, getBranch],
  (project, selectedBranch) => {
    const revisions = project ? project.revisions : {};
    return revisions?.[selectedBranch] ?? [];
  },
);

const getProjectIsFetchingRevisions = createSelector(
  [getProjectByLocator],
  (project) => project ? project.isFetchingRevisions : false,
);

export default {
  getStatus: createSelector(['projects.status']),
  getProjects,
  getProjectList,
  getSortMode,
  getSelectedProjects,
  getBranchesForProject,
  getProjectIsFetchingBranches,
  getRevisionsForProject,
  getProjectIsFetchingRevisions,
};
